import Finance from 'financejs';
import {
  DraftFile,
  FileEntityTypeEnum,
  FileLocationEnum,
  FileTagEnum,
  InvestmentClassEnum,
  InvestmentContributionsObject,
  PaymentComponentEnum,
  ProjectPropertyDataObject,
} from 'src/generated/graphql-types';
import { InvestmentsEndedDataFormatType } from 'src/investorSystem/utils/hooks/useMainGraphsDataFormat';
import store, { dispatch } from 'src/redux/config/configureStore';
import { getFilesForDocumentsCenter } from 'src/redux/slices/fileTagging.service';
import {
  setOverviewConvertedInvestments,
  setOverviewFilteredConvertedInvestments,
  setOverviewFilteredTotals,
  setOverviewTotals,
} from 'src/redux/slices/investor.slice';
import {
  CalculationTypeEnum,
  CurrencyAmount,
  Entity,
  EntityStatusEnum,
  Fund,
  FundProject,
  Investment,
  InvestmentAmount,
  InvestmentGraphql,
  InvestmentHoldingGraphQL,
  InvestmentRefEnum,
  Operator,
  OverviewTotals,
  PROFIMEX_ID,
  ProfCalculationTypeEnum,
  Project,
  StatusCategoryFilterEnum,
  investmentClassesType,
} from 'src/types.d';
import { convertDateToMonthFormat } from 'src/utils/functions/dateServices';
import {
  getOrganizationDetails,
  mergeEntityDetailsForInvestments,
} from 'src/utils/functions/investmentServices';
import { projectsTypeColorDict } from 'src/utils/variables/index';
import {
  checkIfCancelled,
  getCurrencyByInvestment,
  getCurrencySymbol,
  getOverviewCommitments,
  getOverviewTotals,
  setOverviewCurrencyAndInvestments,
} from './currencyServices';
import { getDateApiFormat, getDaysDiff, getDistributionPeriods, isArrayEmpty } from './d.utils';
import { getFundAssets } from './fundServices';
import { convertInvestmentsGraphqlToInvestments } from './investmentServices';
import { isImageMimeType } from './isImageMimeType';
import { getStateFromCoordinates } from './locationServices';
import { displayNumberFormat } from './numberServices';
import { sortArrayByDateAsc } from './sortArrayByKeyAsc';

export const DEFAULT_VIEW_TYPE = 'achieved';

//Get the global currency by the most active amounts of investment and commitments per currency
export function getGlobalCurrency(investments: Investment[]): string {
  if (!investments) return 'USD'; //default currency on empty array

  //Create a dictionary with the currency and total amount+commitment for that currency
  //For example acc['USD'] = 450  - for $300 investment amount and $150 in commitments
  const currenciesObject = investments.reduce((acc, singleInv) => {
    //Get the investment amount + commitment for each currency
    const currency: string = getCurrency(singleInv);
    const amount: number = getAmount(singleInv);
    const commitment: number = getCommitments(singleInv);

    if (!(acc && acc[currency])) acc[currency] = 0; //init currency

    // Add the combined investment amount and commitment to find the most active currency
    acc[currency] += amount + commitment;
    return acc;
  }, {});

  //remove keys with empty values from object
  removeEmptyKeys(currenciesObject);

  // get the currency keys with actual values
  const currencyKeys = Object.keys(currenciesObject);

  // return default USD if no
  if (currencyKeys.length === 0) return 'USD';

  // if only one currency exist return it
  if (currencyKeys.length === 1) return currencyKeys[0];

  // If there are more than a single currency set USD as the default
  return 'USD';

  //NOTE - Save for future use - if global currency would be chosen by the highest amount instead of USD
  // finds the key (=currency) of the highest amount of commitments and investment amounts
  //return getMaxValueKey(currenciesObject);
}

// Removes all keys with 0 or invalid numbers
function removeEmptyKeys(obj: { [key: string]: number }) {
  const allKeys = Object.keys(obj);
  allKeys.forEach(key => {
    if (obj[key] === 0 || Number.isNaN(obj[key])) delete obj[key];
  });
}

// Gets the investment amount from a given investment
function getAmount(investment: Investment) {
  if (!investment?.investmentAmount) return 0;
  return investment.investmentAmount.reduce((partialSum, a) => partialSum + a.value, 0);
}

export function getTotalsFromAggregatedInvestment(investment) {}

/**
 * Gets the total commitment from a given investment (supports v0 and v2)
 * @param investment Given investment with commitments
 * @returns Total commitment from a given investment (supports v0 and v2)
 */
export function getCommitments(investment: Investment, rate: number = 1): number {
  //if exit date has passed return 0
  if (investment.exitDate && new Date(investment.exitDate) < new Date()) return 0;

  const investmentCommitmentsV2 =
    investment.contributions?.reduce((sum, item) => sum + item.commitment * (rate ?? 1), 0) ?? 0;

  //get v2 commitment
  return investmentCommitmentsV2 ?? 0;
}

// Gets the investment currency in text (such as 'USD')
export function getCurrency(investment: Investment) {
  //return USD as default
  if (!investment) return 'USD';

  const entity = getOrganizationDetails(investment);
  return investment?.currency ? investment.currency : entity?.currency ? entity.currency : 'USD';
}

export function getViewType(investments: Investment[]): 'achieved' | 'profimex' {
  if (
    investments?.some(investment =>
      ['b02c1370-4b9d-11ec-b7b9-a7995e4ae954'].includes(investment?.operatorDetails?.id),
    )
  ) {
    return 'profimex';
  }
  return 'achieved';
}

export function isProjectInvestment(investment: Investment): boolean {
  return investment?.ref[0].key === InvestmentRefEnum.projectId;
}

export function isFundInvestment(investment: Investment): boolean {
  return investment?.ref[0].key === InvestmentRefEnum.fundId;
}

export function getInvestmentStatus(investment: Investment): EntityStatusEnum {
  return (getOrganizationDetails(investment) as Entity)?.status;
}

export function isInvestmentStatusPending(investment: Investment): boolean {
  const status = getInvestmentStatus(investment);
  return status?.toLowerCase() === EntityStatusEnum.PENDING;
}

export function getAllProjectsWithInvestmentAmount(investments: Investment[]) {
  return investments.reduce((acc, investment) => {
    if (isProjectInvestment(investment)) {
      const investmentAmount = getNotPendingTotalContributions([investment]);
      const projectDetails = {
        ...getOrganizationDetails(investment),
        investmentAmount,
      };
      return [...acc, projectDetails];
    }
    if (isFundInvestment(investment)) {
      const entity = getOrganizationDetails(investment) as Fund;
      const projects = getFundAssets(entity);
      const fundInvestmentAmount = getNotPendingTotalContributions([investment]);
      const categoriesObject = projects.reduce((acc, x) => {
        const { category } = x;
        if (!(acc && acc[category])) {
          acc[category] = [];
        }
        acc[category].push(x);
        return acc;
      }, {});
      const costumeProjects = projects.map(x => {
        const projectCategory = x.category;
        const numberOfCategories = Number(
          Object.keys(categoriesObject).reduce(
            (acc, category) => acc + Number(categoriesObject[category].length),
            0,
          ),
        );
        const percentage = Number(categoriesObject[projectCategory].length) / numberOfCategories;
        const investmentAmount = Number(Number(fundInvestmentAmount) * percentage);
        return { ...x, investmentAmount };
      });
      return [...acc, ...costumeProjects];
    }
  }, []);
}

//Set 0 for new currencies
export function initCurrencyArr(currency: string, arr: CurrencyAmount[]) {
  if (!arr[currency]) arr[currency] = 0;
}

/**
 * Updates the filtered overview totals and converted investments in the redux store
 * @param investments Given base investment
 * @returns Filtered overview totals for the given investment
 */
export function updateFilteredOverviewTotals(investments: Investment[]): OverviewTotals {
  //filter out cancelled investments
  investments = investments.filter(investment => checkIfCancelled(investment));

  //get the overview currency details
  const { convertedInvestments, convertToUSD } = setOverviewCurrencyAndInvestments(investments);

  //get the overview totals
  let overviewTotals = getOverviewTotals(convertedInvestments);

  //override whether to convert to USD
  overviewTotals.convertToUSD = convertToUSD;

  //override the total commitments with today's rates
  const commitments = getOverviewCommitments(investments, convertToUSD);
  const totalRemainingCommitments = commitments['remaining'];
  overviewTotals = { ...overviewTotals, totalRemainingCommitments };

  //add the accumulated interest
  const investmentTotals = store?.getState()?.investor?.investmentTotals;

  //filter out investment ids that are not in investments by id
  const totalKeys = Object.keys(investmentTotals);
  const filteredKeys = totalKeys.filter(key => investments.some(inv => inv.id === key)) ?? [];
  const filteredInvestmentTotals = filteredKeys.reduce((acc, key) => {
    acc[key] = investmentTotals[key];
    return acc;
  }, {});

  //get all accumulated interest
  const allInvestmentTotals: any[] = Object.values(filteredInvestmentTotals);
  const totalAccumulatedInterest: number = allInvestmentTotals?.reduce((acc, item) => {
    return (acc + item['accumulatedInterest']) as number;
  }, 0);
  overviewTotals = { ...overviewTotals, totalAccumulatedInterest };

  //update in redux
  dispatch(setOverviewFilteredTotals(overviewTotals));
  dispatch(setOverviewFilteredConvertedInvestments(convertedInvestments));

  return overviewTotals;
}

/**
 * Gets the total amount of all investments for overview page and updates the redux store
 * @param investments Given base investment
 * @returns Overview totals for the given investment
 */
export function updateOverviewTotals(investments: Investment[]): OverviewTotals {
  //filter out cancelled investments
  investments = investments?.filter(investment => checkIfCancelled(investment));

  //get the overview currency details
  const { convertedInvestments, convertToUSD } = setOverviewCurrencyAndInvestments(investments);

  //get the overview totals
  let overviewTotals = getOverviewTotals(convertedInvestments);

  //override whether to convert to USD
  overviewTotals.convertToUSD = convertToUSD;

  //override the total commitments with today's rates
  const commitments = getOverviewCommitments(investments, convertToUSD);
  const totalRemainingCommitments = commitments['remaining'];
  overviewTotals = { ...overviewTotals, totalRemainingCommitments };

  //update in redux
  dispatch(setOverviewTotals(overviewTotals));
  dispatch(setOverviewConvertedInvestments(convertedInvestments));

  return overviewTotals;
}

//Get the total for all none pending contribution
export function getNotPendingTotalContributions(investments: Investment[]): number {
  if (!investments?.length) return 0;

  //get only investments which are not pending
  const filteredInvestment = filterNotPendingInvestments(investments);

  //return the sum of all amounts
  return sumAllInvestmentAmounts(filteredInvestment);
}

//Get the total for all started contribution
export function getStartedTotalContributions(investments: Investment[]): number {
  if (!investments?.length) return 0;

  //get only investments which are not pending
  const filteredInvestment = filterStartedInvestments(investments);

  //return the sum of all amounts
  return sumAllInvestmentAmounts(filteredInvestment);
}

//Sums all investment amounts
function sumAllInvestmentAmounts(investments: Investment[]): number {
  if (!investments?.length) return 0;

  //Sum total amount v0
  // const sumV0 = investments.reduce((acc, investmentObj) => {
  //   return (acc += getEntityTotalInvestment(investmentObj.investmentAmount));
  // }, 0);

  //sum total contributions v2
  const sumV2 = investments?.reduce((acc, investmentObj) => {
    return (acc += getSingleSumContributionV2(investmentObj));
  }, 0);

  return sumV2;
}

/**
 * Sums up all the investment's contributions
 * @param investment Given investment with contributions
 * @returns Total of the investment Contributions
 */
export function getSingleSumContributionV2(investment: Investment): number {
  //sum all contributions
  let contributionsSum = 0;

  if (investment && investment.contributions)
    investment.contributions.forEach(contribution => {
      const totalValue = contribution.contribution * (contribution?.currencyRate ?? 1);
      contributionsSum += totalValue;
    });

  return contributionsSum;
}

/**
 * Returns the total amount for a given investment in a unified string format ex. $1.2M
 * @param investment Given investment with contributions
 * @param decimal Number of decimal points
 * @returns Total of the investment Contributions in a unified string format ex. $1.2M
 */
export function getTextFromInvestmentContribution(
  investment: Investment,
  decimal: number = 2,
): string {
  //get the currency and symbol
  const currency = investment.fundDetails?.currency ?? investment.projectDetails?.currency ?? 'USD';
  const currencySymbol = getCurrencySymbol(currency);

  //get the investment amount and format it
  const amount = getSingleSumContributionV2(investment);
  const amountString = displayNumberFormat(amount, currencySymbol, decimal);
  return amountString;
}

//Get total investments from investmentAmounts array
export function getEntityTotalInvestment(investments: InvestmentAmount[]): number {
  return investments?.length
    ? investments.reduce((acc, el) => {
        return acc + el.value;
      }, 0)
    : 0;
}

export function getOrganizationName(investment: Investment): string {
  return getOrganizationDetails(investment)?.name;
}

export function getInvestedInType(investment: Investment): InvestmentRefEnum {
  return investment?.ref[0].key;
}

export function getInvestmentOperatorId(investment: Investment): string {
  const entity = getOrganizationDetails(investment) as Entity;
  return entity?.operatorId ?? entity?.operatorDetails?.id;
}

export function getInvestmentOperatorName(investment: Investment): string {
  return investment?.operatorDetails?.name;
}

export function getInvestmentsId(investment: Investment): string {
  return getOrganizationDetails(investment)?.id;
}

/**
 * Returns the operator object from a given investments
 * @param investments All user investments
 * @returns Operator object from a given investments
 */
export function getOperatorObjectFromInvestments(investments: Investment[]): Operator {
  const singleInvestment = investments?.length > 0 ? investments[0] : null;
  return getInvestmentOperatorObject(singleInvestment);
}

export function getInvestmentOperatorObject(investment: Investment): Operator {
  return investment?.operatorDetails;
}

//Filters all not pending contribution
export function filterNotPendingInvestments(investments: Investment[]): Investment[] {
  return investments.filter(el => !isInvestmentStatusPending(el));
}

//Filters all started contribution
export function filterStartedInvestments(investments: Investment[]): Investment[] {
  return investments.filter(el => isInvestmentStatusStarted(el));
}

//Filters all not pending contribution
export function filterEndedInvestments(investments: Investment[]): Investment[] {
  return investments.filter(el => isInvestmentStatusCompleted(el));
}

export function getInvestmentsEndedDataFormat(
  investorProjects: Investment[],
): InvestmentsEndedDataFormatType | null {
  const totalInvestmentAmount = getNotPendingTotalContributions(investorProjects);
  const totalDistribution = getInvestmentsEndedTotalDistribution(investorProjects);
  return totalDistribution !== 0 ? { totalInvestmentAmount, totalDistribution } : null;
}

export function getInvestmentsEndedTotalDistribution(investorInvestments: Investment[]) {
  const totalDistributionV0 = investorInvestments.reduce((acc, investorInvestment) => {
    if (isInvestmentStatusCompleted(investorInvestment)) {
      const { holdings, manualHoldings } = investorInvestment;

      //merge the holdings collection
      const mergedHolding = mergeHoldings(manualHoldings, holdings);

      const investorInvestmentTotalDistribution = getInvestmentTotalDistribution(mergedHolding);
      return acc + investorInvestmentTotalDistribution;
    }
    return acc;
  }, 0);

  //get version 2 distributions
  const totalDistributionsV2 = getTotalDistributionV2(investorInvestments);

  //add both distributions
  return totalDistributionV0 + totalDistributionsV2;
}

//get Total distribution from a given investment holdings
export function getInvestmentTotalDistribution(
  holdings: InvestmentHoldingGraphQL[],
  rate: number = 1,
) {
  const res = holdings.reduce((acc, x) => {
    return acc + (Number(x.value) || 0) * rate;
  }, 0);
  return res;
}

/**
 * Sums up all distributions in all investments
 * @param investments Given investment with distributions
 * @returns Total distributions for all investment
 */
export function getTotalDistributionV2(investments: Investment[]): number {
  let total = 0;

  //loop each investment and add it's distributions totlas
  if (investments)
    investments.forEach(investment => {
      total += getSingleInvestmentDistributionV2(investment.distributions);
    });

  return total;
}

//get Total distribution from a given investment holdings
export function getSingleInvestmentDistributionV2(
  distributions: ProjectPropertyDataObject[],
): number {
  //get total distributions version 2
  const totalDistributionsV2 = distributions?.reduce(
    (acc, item) => (acc += (item.amount ?? 0) * (item?.currencyRate ?? 1)),
    0,
  );

  return totalDistributionsV2;
}

export function isInvestmentStatusCompleted(investment: Investment) {
  return getInvestmentStatus(investment)?.toLowerCase() === 'completed';
}
export function isInvestmentStatusCCancelled(investment: Investment) {
  return getInvestmentStatus(investment).toLowerCase() === 'cancelled';
}

//Set all Ongoing graphs data
export function setTotalOfGraphs(quarterlyAmounts, investmentAmount: number) {
  //Sort the Quarters
  const quartersKeys = Object.keys(quarterlyAmounts);
  const sortedQuartersList = sortQuartersList(quartersKeys);

  //return distribution and COC for each sorted quarter
  return sortedQuartersList.map(quarter => {
    const distribution = quarterlyAmounts[quarter];
    const cashOnCash = Number((distribution / investmentAmount) * 100).toFixed(1);
    // return { name: quarter, distribution, cashOnCash, equity };
    const customizeDistribution =
      distribution > 999 ? Number(distribution.toFixed(1)) : Number(distribution.toFixed(0));
    return {
      name: quarter,
      distribution: customizeDistribution,
      cashOnCash: Number(cashOnCash),
    };
  });
}

function sortQuartersList(quartersList) {
  const quartersListWithMapping = quartersList
    .map(quarter => {
      const quarterOriginal = quarter;
      const quarterDataKey = Number(getQuarterDataKeyValue(quarter));
      return { quarterOriginal, quarterDataKey };
    })
    .sort((a, b) => a.quarterDataKey - b.quarterDataKey);
  return quartersListWithMapping.map(quarter => quarter.quarterOriginal);
}

function getQuarterDataKeyValue(quarter) {
  const keys = quarter.split('-');
  const quarterNumber = keys[0].slice(-1);
  const year = keys[1];

  return `${year}${quarterNumber}`;
}

//Gets whether investment status is started
export function isInvestmentStatusStarted(investment: Investment) {
  return getInvestmentStatus(investment)?.toLowerCase() === 'started';
}

// Calc Cash on Cash \ ROI
export function calculateCashOnCash(investment: number, distribution: number) {
  if (distribution === 0 || investment === 0) return 0; // can't compute

  return ((distribution / investment) * 100).toFixed(2);
}

export function getProjectIdFromInvestment(investment: Investment) {
  return executeFunctionByProjectOrFund(
    investment,
    getProjectIdFromProjectInvestment,
    getProjectIdsFromFundInvestment,
  );
}

export function getProjectIdFromProjectInvestment(investment: Investment): string {
  return getOrganizationDetails(investment)?.id;
}

export function getProjectIdsFromFundInvestment(investment: Investment): string[] {
  const entity = getOrganizationDetails(investment) as Fund;
  const projects = getFundAssets(entity);
  return projects.map(x => x.id);
}

export function executeFunctionByProjectOrFund(
  investment: Investment,
  projectFunction: Function,
  fundFunction: Function,
) {
  return isProjectInvestment(investment)
    ? projectFunction(investment)
    : isFundInvestment(investment)
    ? fundFunction(investment)
    : null;
}

export function getOrganizationCategory(organization: Fund | Project): string {
  //if it is a fund
  if (organization?.entityType === 'Fund') return 'fund';

  //handling a custom category
  if (organization?.category?.toLowerCase() === 'custom') {
    // if the category exists return the color
    if (organization?.customCategory?.toLowerCase() in projectsTypeColorDict)
      return organization?.customCategory?.toLowerCase();
    //other wise return other
    else return 'other';
  }
  // the defualt
  return organization?.category?.toLowerCase();
}

export function getOperator(investment: Investment): Operator {
  return investment?.operatorDetails;
}

export function getMinimumInvestmentAmount(investment: Investment) {
  // return getOrganizationDetails(investment).minimumInvestmentAmount;
  return null;
}

export function getOrganizationPrimaryImageUrl(investment: Investment): string {
  // return getOrganizationDetails(investment).primaryImageUrl;
  return null;
}

export function getOrganizationLocation(investment: Investment): string {
  return (getOrganizationDetails(investment) as Project)?.location?.address;
}

function getInvestmentData(investmentAmount: InvestmentAmount[]): KpiData {
  return investmentAmount.reduce(
    (investmentData, investment) => {
      const date = new Date(investment.date);
      const distribution = Number(investment.value);
      investmentData.dates.push(date);
      investmentData.values.push(distribution);
      investmentData.total += investment.value;
      return investmentData;
    },
    {
      dates: [],
      values: [],
      total: 0,
    },
  );
}

/**
 * Construct a finance package data from a given investment
 * @param investment given investment to construct the data from
 * @returns Distributions finance package data
 */
export function constructFinanceDataV2(amountsAndDates: any): KpiData {
  //go over all distributions
  return amountsAndDates?.reduce(
    (data, element) => {
      //get date and negative number of contribution
      const date = new Date(element.date);
      const amount = element.amount ?? 0;

      //skip 0 values
      if (amount === 0) return data;

      //add to the new data structure to support finance package
      data.dates.push(date);
      data.values.push(amount);
      data.total += amount;
      return data;
    },
    {
      dates: [],
      values: [],
      total: 0,
    },
  );
}

/**
 * Construct a finance package data from a given investment
 * @param investment given investment to construct the data from
 * @returns Distributions finance package data
 */
export function constructDistributionsFinanceDataV2(investment: Investment): KpiData {
  //map the dates and amounts
  const distributionAndDates = investment?.distributions?.map(distribution => {
    return { date: distribution.date, amount: distribution.amount };
  });

  return constructFinanceDataV2(distributionAndDates);
}

/**
 * Construct a finance package data from a given investment
 * @param investment given investment to construct the data from
 * @returns Contribution finance package data
 */
export function constructContributionFinanceDataV2(investment: Investment): KpiData {
  //map the dates and negative amounts
  const contributionsAndDates = investment?.contributions?.map(singleContribution => {
    return { date: singleContribution.date, amount: singleContribution.contribution };
  });

  return constructFinanceDataV2(contributionsAndDates);
}

interface KpiData {
  dates: Date[];
  values: Number[];
  total: number;
}

function getHoldingsData(holdings: InvestmentHoldingGraphQL[]): KpiData {
  return holdings.reduce(
    (holdingsDataForKpi, item) => {
      const date = new Date(item.date);
      const distribution = Number(item.value);
      holdingsDataForKpi.dates.push(date);
      holdingsDataForKpi.values.push(distribution);
      holdingsDataForKpi.total += distribution;
      return holdingsDataForKpi;
    },
    {
      dates: [],
      values: [],
      total: 0,
    },
  );
}

/**
 * Fetch Profimex KPI metrics by the values existing in the investment object
 */
const calculateProfKPI = (investment: Investment, calculationType: CalculationTypeEnum) => {
  if (calculationType) return investment[ProfCalculationTypeEnum[calculationType]];
  else {
    return investment?.grossCoc || investment?.grossRoi || investment?.grossIrr || null;
  }
};

/**
 * Returns the percentage of the requested calculation type on the given investment
 * @param investment Given investment
 * @param calculationType Type of financial calculation (ex. IRR, ROI)
 * @param isProfKpi Should override with profimex values
 * @returns Percentage of the requested calculation type on the given investment
 */
export function calculateKPI(
  investment: Investment,
  calculationType: CalculationTypeEnum,
  isProfKpi = false,
): number {
  if (isProfKpi) return calculateProfKPI(investment, calculationType);
  const finance = new Finance();
  const { investmentAmount, holdings } = investment;

  //construct the data in a finance format
  const isVersion2 = investment.version === 2;
  const contributionsData = isVersion2
    ? constructContributionFinanceDataV2(investment)
    : getInvestmentData(investmentAmount);
  const distributionsData = isVersion2
    ? constructDistributionsFinanceDataV2(investment)
    : getHoldingsData(holdings);

  //IRR calc needs the contributions values as negative
  const contributionNegativeValues = contributionsData.values.map((value: number) => value * -1);

  const dataDict = {
    ROI: [contributionsData.total, distributionsData.total + contributionsData.total],
    IRR: [contributionsData.total * -1, ...distributionsData.values], //takes the total contribution as negative
    XIRR: [
      [...contributionNegativeValues, ...distributionsData.values], //takes the contributions as negative
      [...contributionsData.dates, ...distributionsData.dates],
      0,
    ],
  };

  try {
    return finance[calculationType](...dataDict[calculationType]);
  } catch (err) {
    console.error(`Could not calculate ${calculationType}:`, err);
    return 0;
  }
}

export async function getAllInvestmentDocuments(
  token,
  role,
  investments: Investment[],
  investor,
  client,
) {
  const docs = await investments.map(async investment => {
    const context = investment.projectDetails
      ? FileEntityTypeEnum.Project
      : investment.fundDetails
      ? FileEntityTypeEnum.Fund
      : null;

    const entity = investment.projectDetails || investment.fundDetails;
    const operator = investment.operatorDetails;
    const projectNameOrFundName = entity?.name;
    const operatorName = operator.name;

    const projectOrFundDocuments = await getFilesForDocumentsCenter(
      token,
      role,
      context,
      {
        operator,
        entity,
        investor,
      },
      client,
    );
    return formatDocumentsArray(projectOrFundDocuments, projectNameOrFundName, operatorName);
  });
  let entityDocs;
  await Promise.all(docs).then(values => {
    entityDocs = values.reduce((acc, value) => {
      if (value?.length) return [...acc, ...value];
      return acc;
    }, []);
  });
  return entityDocs;
}

export async function getAllOperatorUpdateDocumentsNew(token, role, operators, investor) {
  const docs: Promise<any>[] = operators.map(async operator => {
    const files = await getFilesForDocumentsCenter(token, role, FileLocationEnum.Updates, {
      operator,
    });
    const operatorName = operator.name;
    return formatDocumentsArray(files, '', operatorName);
  });
  let operatorDocs;
  await Promise.all(docs).then(values => {
    operatorDocs = values.reduce((acc, valueResponse) => {
      if (valueResponse?.length) return [...acc, ...valueResponse];
      return acc;
    }, []);
  });
  return operatorDocs;
}

// /**
//  * *make each document object match the table format and remove images
//  * @param {*} documents array of documents
//  * @param {*} type the type of the document
//  * @param {*} project the project that the document is related to
//  * @param {*} manager the manager that the document is related to
//  */
export function formatDocumentsArray(documents: DraftFile[], project: string, manager: string) {
  //filter images
  const filteredDocuments = filterDocumentsForDocumentCenter(documents);

  return (
    filteredDocuments?.map(document => formatDocumentForTable(document, project, manager)) ?? []
  );
}

/**
 * Holds the project or fund name and the operator name
 */
export type EntityNameAndOperatorName = {
  entityName: string;
  operatorName: string;
};

/**
 * Holds the project, fund, operator names by their id
 */
export type EntityNamesById = {
  [key: string]: EntityNameAndOperatorName;
};

/**
 * Filters out the images that are not personal files for the document center
 * @param documents All documents for the document center
 * @returns Filtered documents for the document center
 */
function filterDocumentsForDocumentCenter(documents: DraftFile[]): DraftFile[] {
  return (
    documents?.filter(document => {
      //whether the file is a personal file
      const isPersonalFile: boolean = document?.tags?.some(
        tag => tag.type === FileTagEnum.Investor || tag.type === FileTagEnum.Viewer,
      );
      return document?.content && (!isImageMimeType(document.content) || isPersonalFile);
    }) ?? []
  );
}

/**
 * Formats a given document for the document center
 * @param documents Given document files
 * @param entityNamesById All project, fund and operator names dictionary
 * @param operatorName Operator name
 * @returns Formatted document for the document center
 */
export function formatDocumentsForDocumentCenter(
  documents: DraftFile[],
  entityNamesById: EntityNamesById,
) {
  //validate input
  if (!documents || isArrayEmpty(documents)) return [];
  if (!entityNamesById || Object.keys(entityNamesById)?.length === 0) return [];

  //filter images
  const filteredDocuments = filterDocumentsForDocumentCenter(documents);

  //loop over all documents
  return filteredDocuments?.map(document => {
    //get all location ids
    const matchedLocationId: string = document.locations?.find(
      location => location?.entityId && location?.entityId in entityNamesById,
    )?.entityId;

    //try to match the name by the location id
    const matchedLocationName = matchedLocationId
      ? entityNamesById[matchedLocationId]?.entityName
      : null;
    if (matchedLocationName) return formatDocumentForTable(document, matchedLocationName, '');

    //else try by the document entity ids
    //find the matching entity id - by the order of  project -> fund -> operator
    const id: string = document.projectId || document.fundId || document.operatorId;

    //if id is in the dictionary - return the matching name
    const matchingProjectFund: string = id in entityNamesById ? entityNamesById[id].entityName : '';

    //find the matching operator
    const operatorName = id in entityNamesById ? entityNamesById[id].operatorName : '';

    return formatDocumentForTable(document, matchingProjectFund, operatorName);
  });
}

/**
 * Formats a given document for the document center
 * @param document Given document
 * @param project Project name
 * @param manager Operator name
 * @returns File formatted for the document center
 */
export function formatDocumentForTable(document: DraftFile, project: string, manager: string) {
  //remove suggested tag from the tags array
  let tags = document?.tags?.filter(tag => tag.type !== FileTagEnum.SuggestedTag);

  //update the tags in the document
  const docWithTags: DraftFile = { ...document, tags };

  const formattedDoc = {
    ...docWithTags,
    name: document.fileName,
    documentDate: document?.createdAt
      ? // ? convertDateToMonthFormat(new Date(document?.createdAt.$date))
        convertDateToMonthFormat(new Date(document?.createdAt))
      : null,
    type: getFileTagsValuesToString(document),
    project,
    manager,
    tags,
  };

  return formattedDoc;
}

/**
 * Gets tags for display as a text in the row
 * @param file file with tags
 * @returns tags in a text format
 */
function getFileTagsValuesToString(file: DraftFile) {
  const tagsString = file.tags
    ?.filter(
      tag =>
        !tag.hidden &&
        tag.value &&
        tag.type !== FileTagEnum.Viewer &&
        tag.type !== FileTagEnum.SuggestedTag,
    )
    ?.map(tag => {
      if (tag.type === FileTagEnum.Investor) return 'personal document';
      else return tag.value;
    });

  //Make the tags unique
  const uniqueTagsStrings = [...new Set(tagsString)].join(', ');
  return uniqueTagsStrings;
}
export function arrangeInvestorInvestmentsByCategories(investments: Investment[]) {
  const notPendingInvestments = investments?.filter(
    investment => !isInvestmentStatusPending(investment),
  );

  const allProjects = getAllProjectsWithInvestmentAmount(notPendingInvestments);
  return allProjects.reduce((acc, el) => {
    try {
      const { category } = el;
      const categoryLowerCase = category.toLowerCase();
      if (!(acc && acc[categoryLowerCase])) {
        acc[categoryLowerCase] = [];
      }
      acc[categoryLowerCase].push(el);
      return { ...acc };
    } catch (err) {
      return acc;
    }
  }, {});
}

export function arrangeInvestorInvestmentsByArea(investments: Investment[]) {
  const notPendingInvestments = investments.filter(
    investment => !isInvestmentStatusPending(investment),
  );
  const allProjects = getAllProjectsWithInvestmentAmount(notPendingInvestments);
  return allProjects.reduce((acc, el) => {
    try {
      const { address } = el.location;
      const { statesShow } = getStateFromCoordinates(address);
      if (!acc.hasOwnProperty(statesShow)) {
        acc[statesShow] = [];
      }
      acc[statesShow].push(el);
      return { ...acc };
    } catch (err) {
      return acc;
    }
  }, {});
}

/**
 * Helper type for the accumulated interest event
 */
type accumulatedInterestEventType = {
  date: Date;
  type: 'contribution' | 'distribution';
  amount: number;
  percentage?: number; //interest percentage
  endDate?: Date; //for the contribution
  isCompounding?: boolean; //for the distribution
  matchClass?: investmentClassesType; //for the distribution
};

/**
 * Create event structure for the calculating the interest
 */
function createEventStructure(
  contributions: InvestmentContributionsObject[],
  distributions: ProjectPropertyDataObject[],
  matchingClasses: investmentClassesType[],
): accumulatedInterestEventType[] {
  //create a single array for the events
  const events: accumulatedInterestEventType[] = [];

  //add the contributions
  contributions &&
    contributions.forEach(contribution => {
      const contributionDate = new Date(contribution.date);
      const contributionClosingDate = contribution.closingDate
        ? new Date(contribution.closingDate)
        : null;
      const currencyRate: number = contribution.currencyRate ?? 1;
      const percentage: number = contribution.percentage ? contribution.percentage / 100 : 0;
      const matchingClass: investmentClassesType | undefined = matchingClasses.find(
        match => match.name?.toLowerCase() === contribution.investmentClassName?.toLowerCase(),
      );
      const isCompounding: boolean = matchingClass?.isCompounding ?? false;
      events.push({
        date: contributionDate,
        type: 'contribution',
        amount: contribution.contribution * currencyRate,
        percentage,
        isCompounding,
        matchClass: matchingClass,
      });
      if (contributionClosingDate)
        events.push({
          date: contributionClosingDate,
          type: 'distribution',
          amount: contribution.contribution * currencyRate,
          endDate: contributionClosingDate,
          isCompounding,
          matchClass: matchingClass,
        });
    });

  //add the distributions
  distributions &&
    distributions.forEach(distribution => {
      const currencyRate: number = distribution.currencyRate ?? 1;
      const amount = distribution.amount ?? 0;
      const expenses = distribution.expenses ?? 0;
      const netAmount = amount - expenses;
      events.push({
        date: new Date(distribution.payDueDate),
        type: 'distribution',
        amount: netAmount * currencyRate,
      });
    });

  //sort the events by date
  events.sort((a, b) => a.date.getTime() - b.date.getTime());

  return events;
}

/**
 * Gets the accumulated interest for a given investment for debt type with balloon distribution period
 * @param investment Given investment
 * @returns Accumulated interest for the given investment
 */
export function getAccumulatedInterest(investment: Investment) {
  // Validate input
  if (!investment || !investment.contributions || investment.contributions.length === 0) return 0;

  // Get the equity types class names
  let matchingClasses: investmentClassesType[] =
    investment?.entityDetails?.investmentClasses?.map(investmentClass =>
      investmentClass.type === InvestmentClassEnum.Debt ? investmentClass : null,
    ) ?? [];

  // Filter the null values
  matchingClasses = matchingClasses.filter(valid => valid);

  // Sort out any contribution that doesn't match the investment class
  const validContributions =
    investment.contributions.filter(contribution =>
      matchingClasses?.some(
        match => match.name?.toLowerCase() === contribution.investmentClassName?.toLowerCase(),
      ),
    ) ?? [];

  // Sort out any distribution that doesn't match the investment class or are not principal
  const validDistributions =
    investment.distributions.filter(
      distribution =>
        distribution.investmentClassType === InvestmentClassEnum.Debt &&
        distribution.paymentComponent === PaymentComponentEnum.Principal,
    ) ?? [];

  // Sort the contributions and distributions by date
  validContributions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  validDistributions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Create a single array for the events
  const events: accumulatedInterestEventType[] = createEventStructure(
    validContributions,
    validDistributions,
    matchingClasses,
  );

  //NOTE: workaround for allowing us to check the accumulated value for the last date
  // Get the calculation end date from local storage or use the current date
  const calcLastDateStr = localStorage.getItem('calc_last_date');
  const calcLastDate = calcLastDateStr ? new Date(calcLastDateStr) : new Date();
  calcLastDate.setHours(0, 0, 0, 0);

  // Loop through all events and calculate the interest
  let totalInterest = 0;
  let currentPercentage = 0;
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    const interestRate = event.percentage ? event.percentage : currentPercentage;

    // If event date is later than the calculation end date, skip it
    if (event.date?.getTime() > calcLastDate.getTime()) continue;

    // Set the end date for the calculation
    const nextDate = event.endDate ? event.endDate : calcLastDate;

    // Remove hours, minutes, and seconds from the date
    nextDate.setHours(0, 0, 0, 0);
    event.date.setHours(0, 0, 0, 0);
    const daysBetween = getDaysDiff(event.date, nextDate);

    // Calculate interest for the period
    let interestAmount = event.amount * interestRate * (daysBetween / 365);

    // if the contribution is compounding, add the interest to the amount
    if (event.isCompounding) {
      // Determine compounding periods (default to 12 for monthly)
      const compoundingPeriods = getDistributionPeriods(event?.matchClass);
      // Calculate compound interest using formula: P(1 + r/n)^(nt) - P
      // where P is principal, r is annual rate, n is compounding periods per year, t is time in years
      // Get time in years
      const timeInYears = daysBetween / 365;
      const compoundFactor = Math.pow(
        1 + interestRate / compoundingPeriods,
        compoundingPeriods * timeInYears,
      );
      // Calculate the theoretical total with compound interest
      interestAmount = event.amount * (compoundFactor - 1);
    }

    // Update principal based on the event
    if (event.type === 'contribution') {
      totalInterest += interestAmount;
      currentPercentage = event.percentage; // Update the current percentage
    } else if (event.type === 'distribution') totalInterest -= interestAmount;
  }

  // Remove all interest payments that were made
  const interestPayments = investment.distributions.filter(
    distribution =>
      distribution.investmentClassType === InvestmentClassEnum.Debt &&
      distribution.paymentComponent === PaymentComponentEnum.Interest &&
      new Date(distribution.date) < calcLastDate,
  );

  // Sum all interest payments
  const totalInterestPayments = interestPayments.reduce(
    (total, payment) => total + payment.amount,
    0,
  );

  // Remove all interest payments from the total interest
  totalInterest = totalInterest - totalInterestPayments;
  if (totalInterest < 0) totalInterest = 0;

  return totalInterest;
}

/**
 * @description calculates the totals for the investments
 */
export function arrangeInvestmentTotals(investments: Investment[]) {
  if (!investments) return {};

  const investmentTotals = {};

  //merge the entity details for all investments
  const mergedInvestment: Investment[] = mergeEntityDetailsForInvestments(investments);

  //looping through all investments
  mergedInvestment.forEach(investment => {
    const currency = getCurrencyByInvestment(investment);
    let commitments = 0;
    let contributions = 0;
    let distributions = 0;
    let principalsReturns = 0;
    let equityReturns = 0;
    let interestReturns = 0;
    let investedCapital = 0;
    let accumulatedInterest = 0;
    let interestDistribution = 0;

    // calculating the commitments and contributions
    if (investment?.contributions)
      investment.contributions.forEach(singleContribution => {
        // calculating the contributions
        if (singleContribution?.contribution)
          contributions += singleContribution.contribution * (singleContribution.currencyRate ?? 1);

        // calculating the commitments
        if (singleContribution?.commitment)
          commitments += singleContribution.commitment * (singleContribution.currencyRate ?? 1);
      });

    // calculating the distributions
    investment.distributions.forEach(distribution =>
      distribution.amount ? (distributions += distribution.amount * distribution.currencyRate) : 0,
    );

    // calculating the interest distributions
    investment.distributions.forEach(distribution =>
      distribution.amount
        ? (interestDistribution +=
            distribution.paymentComponent === PaymentComponentEnum.Interest //get just the interest distribution type
              ? distribution.amount * distribution.currencyRate
              : 0)
        : 0,
    );

    // calculating the principle returns
    investment.distributions.forEach(distribution =>
      distribution.amount
        ? (principalsReturns +=
            distribution.paymentComponent === PaymentComponentEnum.Principal //get just the principle distribution type
              ? distribution.amount * distribution.currencyRate
              : 0)
        : 0,
    );

    // calculating the principle distribution
    interestDistribution = distributions - principalsReturns;

    // calculating the equity returns
    investment.distributions.forEach(distribution =>
      distribution.amount
        ? (equityReturns +=
            distribution.investmentClassType === InvestmentClassEnum.Equity //get just the equity distribution type
              ? distribution.amount * distribution.currencyRate
              : 0)
        : 0,
    );

    // calculating the interest returns
    investment.distributions.forEach(distribution =>
      distribution.amount
        ? (interestReturns +=
            distribution.paymentComponent === PaymentComponentEnum.Interest //get just the interest distribution type
              ? distribution.amount * distribution.currencyRate
              : 0)
        : 0,
    );

    // calculating the owned interest on a balloon debt
    accumulatedInterest = getAccumulatedInterest(investment);

    const openCommitments = commitments - contributions > 0 ? commitments - contributions : 0;

    //calc the total principal returns
    investedCapital = contributions - principalsReturns;

    if (!investmentTotals[investment.id]) investmentTotals[investment.id] = {};
    // adding the totals to the investment totals
    investmentTotals[investment.id]['commitments'] = commitments;
    investmentTotals[investment.id]['contributions'] = contributions;
    investmentTotals[investment.id]['distributions'] = distributions;
    investmentTotals[investment.id]['interestDistribution'] = interestDistribution;
    investmentTotals[investment.id]['openCommitments'] = openCommitments;
    investmentTotals[investment.id]['currency'] = currency;
    investmentTotals[investment.id]['principleReturns'] = principalsReturns;
    investmentTotals[investment.id]['investedCapital'] = investedCapital;
    investmentTotals[investment.id]['accumulatedInterest'] = accumulatedInterest;
    investmentTotals[investment.id]['equityReturns'] = equityReturns;
  });

  return investmentTotals;
}

export function getLatLngFromProject(project: Project | FundProject): {
  lat: number;
  lng: number;
} {
  const lat = project?.location?.latitude;
  const lng = project?.location?.longitude;
  return { lat, lng };
}

/**
 * Gets the holdings data from the investment including manual holdings
 * @param investment Given investment with manual holdings and holdings
 * @returns Holdings and manual holdings data sorted by date
 */
const getArrangeHoldingsData = (investment: InvestmentGraphql) => {
  //if date is in manual holdings, take the value from manual holdings, else take the value from holdings
  const combinedHoldings = mergeHoldings(investment.manualHoldings, investment.holdings);

  //combine holding by date and sum the values
  const holdings = combinedHoldings.reduce((acc, holding) => {
    //get the date only in string format
    const date: string = getDateApiFormat(holding?.date);
    const value = holding?.value;
    if (!acc[date]) acc[date] = 0;

    acc[date] += Number(value) ?? 0;
    return acc;
  }, {});

  //convert the holdings object to array
  const holdingsArray = Object.keys(holdings).map(date => {
    return { date: new Date(date), value: holdings[date] };
  });

  //return the sorted holdings
  return holdingsArray;
};

/**
 * Get both holdings and manual holdings from server, sorted by date
 * @param investment Investment received from server
 * @returns Merged holdings and manual holdings collection sorted by date
 */
export function getAllHoldingsFromInvestmentGraphql(investment: InvestmentGraphql) {
  //get holdings collection with date fixed from manual holding
  const holdings =
    investment?.holdings?.length || investment?.manualHoldings?.length
      ? getArrangeHoldingsData(investment)
      : [];

  //sort by date
  return holdings ? sortArrayByDateAsc(holdings, 'date') : [];
}

/**
 * Returns a merged holdings with unique date values
 * @param manual Manual holdings
 * @param automatic Holdings calculated by the platform
 * @returns Merged holdings with unique date values
 */
export function mergeHoldings(
  manual: InvestmentHoldingGraphQL[],
  automatic: InvestmentHoldingGraphQL[],
): InvestmentHoldingGraphQL[] {
  //validate input
  if (isArrayEmpty(manual)) return automatic ?? [];
  if (isArrayEmpty(automatic)) return manual ?? [];

  const merged: InvestmentHoldingGraphQL[] = [];

  //copy the manual collection
  manual.forEach(holding => merged.push(holding));

  //add unique automatic holdings
  automatic.forEach(holding => {
    //skip if the automatic date already exist in the manual collection
    if (
      manual.some(
        manualHolding =>
          new Date(holding.date).toLocaleDateString() ===
          new Date(manualHolding.date).toLocaleDateString(),
      )
    )
      return;

    //else this is a unique date add it up
    merged.push(holding);
  });

  return merged;
}

export function arrangeInvestments(investments: Investment[], operator?: Operator) {
  const holdings = (investments as InvestmentGraphql[])?.map(x => ({
    ...x,
    holdings: getAllHoldingsFromInvestmentGraphql(x),
  }));

  const convertedInvestments = investments ? convertInvestmentsGraphqlToInvestments(holdings) : [];
  const investmentDetails = convertedInvestments?.length
    ? convertedInvestments.map(investment => {
        const entity = { ...getOrganizationDetails(investment) };
        if (investment?.ref[0]?.key === InvestmentRefEnum.projectId) {
          entity.entityType = 'Project';
        } else {
          entity.entityType = 'Fund';
        }
        const arrangedEntity = operator ? { ...entity, operatorDetails: operator } : entity;
        const arrangedInvestment: Investment = operator
          ? {
              ...investment,
              operatorDetails: operator,
            }
          : investment;
        const dict = {
          project: () => {
            arrangedInvestment['projectDetails'] = arrangedEntity as Project;
          },
          fund: () => {
            arrangedInvestment['fundDetails'] = arrangedEntity as Fund;
          },
        };

        dict[arrangedEntity?.entityType?.toLowerCase()]();
        return arrangedInvestment;
      })
    : [];
  return investmentDetails;
}

/**
 * Filters the investments by the given entity view and category filter
 * @param investments All user investments
 * @param entityView Filter by entity view
 * @param categoryFilter Category filter
 * @returns Filtered investments by the given entity view and category filter
 */
export function filterInvestmentByFilters(
  investments: Investment[],
  entityView: any,
  categoryFilter: StatusCategoryFilterEnum,
  shouldFilterProfimexTransferred = false,
) {
  //todo idan create enum and a singel object to all filters
  //todo idan go over all current usages and use filterInvestmentByFilters instead of the current implementation - only after all new coponents are written
  // Filter by entity view
  const filteredByEntity =
    entityView.name === 'All'
      ? investments
      : investments.filter(investment => investment.legalEntityId === entityView.id);

  //For profimex only - filter any contribution or distribution if this is in the notes
  const PROFIMEX_DISREGARD_NOTES = ['transfer to', 'direct investment'];

  //For profimex only - filter any contribution or distribution that is transferred to another entity
  const filterProfimexTransferred = !shouldFilterProfimexTransferred
    ? filteredByEntity
    : filteredByEntity.map(investment => {
        if (investment.operatorDetails?.id !== PROFIMEX_ID) return investment; //filter only profimex

        //filter only transferred to profimex
        const contributions =
          investment.contributions?.filter(
            contribution =>
              !PROFIMEX_DISREGARD_NOTES.some(note =>
                contribution.notes?.toLowerCase()?.trim()?.includes(note),
              ),
          ) ?? [];
        const distributions =
          investment.distributions?.filter(
            distribution =>
              !PROFIMEX_DISREGARD_NOTES.some(note =>
                distribution.notes?.toLowerCase()?.trim()?.includes(note),
              ),
          ) ?? [];

        //update the investment with the new contributions and distributions
        return { ...investment, contributions, distributions };
      });

  // Filter by category
  const filteredByCategory = filterProfimexTransferred.filter(investment => {
    if (categoryFilter === StatusCategoryFilterEnum.ALL) return true;
    const status = getInvestmentStatus(investment)?.toLowerCase();
    return categoryFilter?.toString()?.toLowerCase() === status;
  });

  return filteredByCategory;
}
